import { Fragment, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SearchFilterContext, {
  INITIAL_SEARCH_FILTERS,
  SearchFilterProps,
} from '../hooks/search'
import { Tab, TabPanels, TabPanel, TabGroup, TabList } from '@headlessui/react'
import SelfPacedCourses from '../components/SelfPacedCourses'
import VirtualInstructorCourses from '../components/VirtualInstructorCourses'
import InstructorLedCourses from '../components/InstructorLedCourses'
import { useSearchParams } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardAction,
  CardTitle,
  CardHeader,
} from '@/components/ui/card'
import { XMarkIcon } from '@heroicons/react/24/outline'
import aseTrainingImage from '@/images/ase-training.jpg'
// import SiteStatusBanner from '@/components/SiteStatusBanner'
import SkillTracksComingSoonBanner from '@/components/SkillTracksComingSoonBanner'
import { MotionButton } from '@/components/ui/motion-button'
import Banner from '@/components/Banner/Banner'

const Home: React.FC = () => {
  const { t } = useTranslation('pages')

  const [searchParams, setSearchParams] = useSearchParams()
  const { search, sessionData, loading } = useContext(SearchFilterContext)
  const [searchResults, setSearchResults] = useState<SearchFilterProps[]>([])

  const tabIndexMapping: Record<'vilt' | 'ilt' | 'olt', number> = {
    vilt: 0,
    ilt: 1,
    olt: 2,
  }

  // The URL is the single source of truth for the active tab. Keeping a
  // parallel useState raced with react-router v7's transition-wrapped
  // setSearchParams and made the tabs flicker.
  const courseTypeParam = searchParams.get('courseType') as
    | 'vilt'
    | 'ilt'
    | 'olt'
    | null
  const activeCourseType: 'vilt' | 'ilt' | 'olt' =
    courseTypeParam && courseTypeParam in tabIndexMapping
      ? courseTypeParam
      : 'vilt'

  const onSearch = (values: SearchFilterProps) => {
    const results = search({ ...values, courseType: activeCourseType })
    setSearchResults(results)
    setSearchParams({
      ...convertFiltersToQueryParams(values),
      courseType: activeCourseType,
    })
  }

  const onReset = () => {
    setSearchParams({ courseType: activeCourseType })
    const resetFilters = {
      ...INITIAL_SEARCH_FILTERS,
      courseType: activeCourseType,
    }
    setSearchResults(search(resetFilters))
  }

  const convertParamsToFilters = (
    params: URLSearchParams
  ): SearchFilterProps => {
    return {
      ...INITIAL_SEARCH_FILTERS,
      ...Object.fromEntries(params.entries()),
    } as SearchFilterProps
  }

  const convertFiltersToQueryParams = (
    filters: SearchFilterProps
  ): Record<string, string> => {
    return Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value?.trim())
    )
  }

  useEffect(() => {
    if (!loading && sessionData.length > 0) {
      setSearchResults(
        search({
          ...INITIAL_SEARCH_FILTERS,
          courseType: activeCourseType,
        })
      )
    }
  }, [loading, sessionData, activeCourseType])

  const instructorList = sessionData.reduce<string[]>((unique, item) => {
    if (item.instructors) {
      item.instructors.forEach(({ firstname, lastname }) => {
        const fullName = `${firstname} ${lastname}`
        if (!unique.includes(fullName)) unique.push(fullName)
      })
    }
    return unique
  }, [])

  const filteredSessionData = sessionData.filter(
    (course) => course.courseType === activeCourseType
  )

  const [showAlert, setShowAlert] = useState(true)

  return (
    <main className="grow display-linebreak main-container-height">
      {/* <SiteStatusBanner /> */}
      {!showAlert && (
        <Card className="p-4 bg-primary lg:max-h-60 lg:gap-0">
          <CardHeader className="flex text-white justify-between">
            <CardTitle className="mx-auto">
              ASE Training Managers Council annual survey
            </CardTitle>
            <CardAction>
              <button
                onClick={() => setShowAlert(false)}
                aria-label="Close alert"
              >
                <XMarkIcon aria-hidden="true" className="size-5 text-white" />
              </button>
            </CardAction>
          </CardHeader>
          <CardContent className="mx-auto text-white">
            Make your voice heard! Take the{' '}
            <a href="https://www.surveymonkey.com/r/JK9G63G">
              ASE Training Managers Council annual survey
            </a>{' '}
            to help shape the future of training across the industry.{' '}
            <MotionButton
              className='text-primary'
              style={{ background: "white" }}
              onClick={() => window.open("https://www.surveymonkey.com/r/JK9G63G", "_blank")}
            >
              Click here
            </MotionButton>
            <img
              className="max-w-72 h-auto mx-auto mt-4 p-4 bg-white"
              src={aseTrainingImage}
            />
          </CardContent>
        </Card>
      )}
      {/* <SiteStatusBanner /> */}
      <div className="py-8 px-4 sm:px-12 container mx-auto shadow-md">
        <section className="container mx-auto">
          <SkillTracksComingSoonBanner />
          <h2 className="text-grays-dark text-2xl font-bold mb-4">
            {t('index.title')}
          </h2>
          <p className="text-grays-dark font-normal mb-8">
            {t('index.description')}
          </p>
        </section>
        <TabGroup
          selectedIndex={tabIndexMapping[activeCourseType]}
          onChange={(index) => {
            const newCourseType = (
              Object.keys(tabIndexMapping) as ('ilt' | 'vilt' | 'olt')[]
            )[index]
            setSearchParams({ courseType: newCourseType })
          }}
        >
          <div className="p-4 bg-primary rounded-t shadow gap-4 w-full">
            <h2 className="mb-2 font-semibold text-white">
              Search by Course Type:
            </h2>

            <TabList className="flex gap-4 w-full">
              {[
                { type: 'vilt', fallback: 'Virtual Classroom Courses' },
                { type: 'ilt', fallback: 'In-person Instructor Led Courses' },
                { type: 'olt', fallback: 'Online Self Paced Courses' },
              ].map(({ type, fallback }) => (
                <Tab key={type} as={Fragment}>
                  {({ selected }) => (
                    <button
                      className={
                        selected
                          ? 'active-tab bg-secondary cursor-pointer border-secondary basis-1/3'
                          : 'inactive-tab bg-white cursor-pointer basis-1/3'
                      }
                    >
                      {t(`index.tabs.${type}`, fallback)}
                    </button>
                  )}
                </Tab>
              ))}
            </TabList>
          </div>

          <TabPanels>
            <TabPanel>
              <VirtualInstructorCourses
                searchParams={searchParams}
                instructorList={instructorList}
                searchResults={searchResults}
                onSearch={onSearch}
                onReset={onReset}
                activeCourseType={activeCourseType}
                loading={loading}
              />
            </TabPanel>
            <TabPanel>
              <InstructorLedCourses
                searchParams={searchParams}
                instructorList={instructorList}
                searchResults={searchResults}
                onSearch={onSearch}
                onReset={onReset}
                activeCourseType={activeCourseType}
                loading={loading}
              />
            </TabPanel>

            <TabPanel>
              <SelfPacedCourses />
            </TabPanel>
          </TabPanels>
        </TabGroup>
      </div>
    </main>
  )
}

export default Home
